import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import type { IEmailCampaign } from "api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns";

export type TEmailCampaignAccordionSectionIndex = 0 | 1 | 2;

type TAudienceSectionSnapshot = {
  kind: "audience";
  selectedAudienceIds: number[];
};

type TFromSectionSnapshot = {
  kind: "from";
  from_name: string;
  from_address: string;
};

type TSubjectSectionSnapshot = {
  kind: "subject";
  subject: string;
  text_preview: string;
};

type TSectionSnapshot =
  | TAudienceSectionSnapshot
  | TFromSectionSnapshot
  | TSubjectSectionSnapshot;

export type TEmailCampaignAccordionSnapshots = Partial<
  Record<TEmailCampaignAccordionSectionIndex, TSectionSnapshot>
>;

export function captureEmailCampaignAccordionSectionOpen(
  sectionIndex: TEmailCampaignAccordionSectionIndex,
  snapshots: TEmailCampaignAccordionSnapshots,
  data: {
    selectedAudienceIds: number[];
    campaign: IEmailCampaign;
  },
): TEmailCampaignAccordionSnapshots {
  const next = { ...snapshots };

  if (sectionIndex === 0) {
    next[0] = {
      kind: "audience",
      selectedAudienceIds: [...data.selectedAudienceIds],
    };
  } else if (sectionIndex === 1) {
    next[1] = {
      kind: "from",
      from_name: data.campaign.from_name ?? "",
      from_address: data.campaign.from_address ?? "",
    };
  } else {
    next[2] = {
      kind: "subject",
      subject: data.campaign.subject ?? "",
      text_preview: data.campaign.text_preview ?? "",
    };
  }

  return next;
}

export function revertEmailCampaignAccordionSection<T extends { campaign: IEmailCampaign }>(
  sectionIndex: TEmailCampaignAccordionSectionIndex,
  snapshot: TSectionSnapshot | undefined,
  setSelectedAudienceIds: Dispatch<SetStateAction<number[]>>,
  setCampaignState: Dispatch<SetStateAction<T>>,
): void {
  if (snapshot == null) {
    return;
  }

  if (sectionIndex === 0 && snapshot.kind === "audience") {
    setSelectedAudienceIds(snapshot.selectedAudienceIds);
    return;
  }

  if (sectionIndex === 1 && snapshot.kind === "from") {
    setCampaignState(prev => ({
      ...prev,
      campaign: {
        ...prev.campaign,
        from_name: snapshot.from_name,
        from_address: snapshot.from_address,
      },
    }));
    return;
  }

  if (sectionIndex === 2 && snapshot.kind === "subject") {
    setCampaignState(prev => ({
      ...prev,
      campaign: {
        ...prev.campaign,
        subject: snapshot.subject,
        text_preview: snapshot.text_preview,
      },
    }));
  }
}

export function emailCampaignAccordionSectionLifecycle<T extends { campaign: IEmailCampaign }>(options: {
  sectionIndex: TEmailCampaignAccordionSectionIndex;
  snapshotsRef: MutableRefObject<TEmailCampaignAccordionSnapshots>;
  getStep2Fields: () => { selectedAudienceIds: number[]; campaign: IEmailCampaign };
  setSelectedAudienceIds: Dispatch<SetStateAction<number[]>>;
  setCampaignState: Dispatch<SetStateAction<T>>;
}): { onOpen: () => void; onCloseWithoutSave: () => void } {
  const { sectionIndex, snapshotsRef, getStep2Fields, setSelectedAudienceIds, setCampaignState } = options;

  return {
    onOpen: () => {
      snapshotsRef.current = captureEmailCampaignAccordionSectionOpen(
        sectionIndex,
        snapshotsRef.current,
        getStep2Fields(),
      );
    },
    onCloseWithoutSave: () => {
      revertEmailCampaignAccordionSection(
        sectionIndex,
        snapshotsRef.current[sectionIndex],
        setSelectedAudienceIds,
        setCampaignState,
      );
    },
  };
}
